/**
 * RevealObserver — global observer for `[data-r]` elements.
 *
 * Mount once at app root. Observes every element on the page with
 * `data-r`. Adds `.in` when each enters the viewport so the CSS transition
 * in src/index.css fires. Watches DOM mutations for SPA route changes.
 *
 * Renders null — pure side-effect mount.
 *
 * Provenance: paperclip/context/MAKO_PATTERN_LANDING_PLAYBOOK_2026-05-13.md
 */
import { useEffect } from "react";

export const RevealObserver = () => {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("IntersectionObserver" in window)) {
      document
        .querySelectorAll("[data-r]")
        .forEach((el) => el.classList.add("in"));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            requestAnimationFrame(() => entry.target.classList.add("in"));
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -6% 0px", threshold: 0.01 },
    );

    const observed = new WeakSet<Element>();
    const observeNew = () => {
      document.querySelectorAll<HTMLElement>("[data-r]").forEach((el) => {
        if (observed.has(el) || el.classList.contains("in")) return;
        observed.add(el);
        io.observe(el);
      });
    };

    observeNew();

    const safety = window.setTimeout(() => {
      document
        .querySelectorAll<HTMLElement>("[data-r]:not(.in)")
        .forEach((el) => {
          const rect = el.getBoundingClientRect();
          if (rect.top < window.innerHeight) el.classList.add("in");
        });
    }, 1200);

    const mo = new MutationObserver(() => observeNew());
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
      window.clearTimeout(safety);
    };
  }, []);

  return null;
};
